/**
 * Health Products price maths. Mirrors place_product_order() in the database,
 * which recalculates everything and rejects the order if totals differ.
 */
export type PricedLine = { gstPaise: number; grossPaise: number };

/** Round-half-up of num/den for non-negative integers (matches Postgres round()). */
function roundDiv(num: number, den: number): number {
  return Math.floor((2 * num + den) / (2 * den));
}

export function priceLine(unitPaise: number, qty: number, gstRatePercent: number, includesGst: boolean): PricedLine {
  const base = unitPaise * qty;
  const r = Math.round(gstRatePercent * 100); // basis points keep the maths integer
  if (includesGst) return { gstPaise: roundDiv(base * r, 10000 + r), grossPaise: base };
  const gst = roundDiv(base * r, 10000);
  return { gstPaise: gst, grossPaise: base + gst };
}

export type ShippingConfig = {
  shipping_model: "free" | "flat" | null;
  flat_shipping_paise: number | null;
  free_shipping_min_paise: number | null;
};

export function shippingFor(itemsTotalPaise: number, s: ShippingConfig): number {
  if (s.shipping_model !== "flat") return 0;
  if (s.free_shipping_min_paise != null && itemsTotalPaise >= s.free_shipping_min_paise) return 0;
  return s.flat_shipping_paise ?? 0;
}

export function cartTotals(
  lines: Array<{ unitPaise: number; qty: number; gstRatePercent: number; includesGst: boolean }>,
  s: ShippingConfig,
) {
  let items = 0;
  let gst = 0;
  for (const l of lines) {
    const p = priceLine(l.unitPaise, l.qty, l.gstRatePercent, l.includesGst);
    items += p.grossPaise;
    gst += p.gstPaise;
  }
  const shipping = shippingFor(items, s);
  return { itemsTotalPaise: items, gstTotalPaise: gst, shippingPaise: shipping, totalPaise: items + shipping };
}

export const CHECKOUT_BLOCKER_TEXT: Record<string, string> = {
  settings_missing: "Online ordering for health products has not been set up yet.",
  checkout_disabled: "Online ordering for health products is not open yet.",
  shipping_not_configured: "Shipping charges have not been set up yet.",
  shipping_fee_missing: "The shipping fee has not been set up yet.",
  delivery_area_not_configured: "Delivery areas have not been set up yet.",
  no_payment_method: "No payment method is switched on yet.",
};

export function orderErrorText(message: string): string {
  const m = message || "";
  if (m.includes("sign_in_required")) return "Please sign in to place your order.";
  if (m.includes("checkout_unavailable")) return "Online ordering is not available right now.";
  if (m.includes("payment_method_unavailable")) return "That payment method is not available.";
  if (m.includes("invalid_contact")) return "Please enter your name and a valid 10-digit mobile number.";
  if (m.includes("invalid_address")) return "Please complete your address and 6-digit pincode.";
  if (m.includes("area_not_served")) return "Sorry, we don't deliver to that pincode yet.";
  if (m.includes("insufficient_stock")) return `Not enough stock: ${m.split("insufficient_stock:")[1] ?? "an item"}. Please reduce the quantity.`;
  if (m.includes("product_unavailable")) return "One of the products is no longer available. Please review your cart.";
  if (m.includes("price_changed")) return "Prices have changed. Please review your cart and try again.";
  if (m.includes("cash_minimum_not_met")) return "This order is below the minimum for cash payment.";
  if (m.includes("invalid_quantity") || m.includes("invalid_cart")) return "Please check the quantities in your cart.";
  return "We couldn't place your order. Please try again.";
}
